/**
 * Teksten van de pane-schil in het Nederlands en Engels (Store-voorbereiding, 5 oktober 2026: de
 * reviewers testen in een Engelse Excel). Zuiver, zonder React, zodat nextStep.ts en de engine-tests
 * het ook gebruiken. Componenten lezen de taal met `useLang()` (src/shared/i18n/LangContext): de
 * provider van de pane, of zonder provider de taal van Excel.
 */

import type { Lang } from "../../i18n/language";

export type { Lang };

const TEXT = {
  close: { NL: "Sluiten", EN: "Close" },
  closeEsc: { NL: "Sluiten (Esc)", EN: "Close (Esc)" },
  open: { NL: "Openen →", EN: "Open →" },
  nextStep: { NL: "volgende stap", EN: "next step" },
  clickOpenMenu: { NL: "klik om het menu te openen", EN: "click to open the menu" },
  clickPrefix: { NL: "klik", EN: "click" },
  openMenu: { NL: "Menu openen", EN: "Open the menu" },
  helpHint: {
    NL: "Klik op een onderstreepte term voor uitleg.",
    EN: "Click an underlined term for an explanation.",
  },
  helpCollapse: { NL: "Uitleg dichtklappen", EN: "Collapse the explanation" },
  helpExpand: { NL: "Uitleg openklappen", EN: "Expand the explanation" },
  collapse: { NL: "Dichtklappen", EN: "Collapse" },
  expand: { NL: "Openklappen", EN: "Expand" },
  explanation: { NL: "Uitleg", EN: "Explanation" },
  of: { NL: "van", EN: "of" },
  stop: { NL: "Stop", EN: "Stop" },
  stopTitle: {
    NL: "Stopt na de lopende stap; wat al klaar is blijft staan.",
    EN: "Stops after the current step; what is already done is kept.",
  },
  supportDetails: { NL: "Details voor ondersteuning", EN: "Details for support" },
  closeMessage: { NL: "Melding sluiten", EN: "Close the message" },
  moreOptions: { NL: "Meer opties", EN: "More options" },
  reload: { NL: "Herladen", EN: "Reload" },
  reloadTitle: { NL: "De pane opnieuw laden", EN: "Reload the pane" },
  about: { NL: "Over", EN: "About" },
  aboutTitle: { NL: "Versie en build tonen", EN: "Show version and build" },
  version: { NL: "versie", EN: "version" },
  switchLang: { NL: "Switch to English", EN: "Wissel naar Nederlands" },
  // Keuzelijst klantmodel (ModelPicker, taak #2762).
  dsLabel: { NL: "Gegevensbron", EN: "Data source" },
  dsNone: { NL: "geen model gekozen", EN: "no model chosen" },
  dsPick: { NL: "Kies model…", EN: "Choose model…" },
  dsPickTitle: {
    NL: "Het klantmodel in Power BI waar de betalingen uit komen. Je ziet alleen modellen waar je in Power BI toegang toe hebt.",
    EN: "The client model in Power BI the payments come from. You only see models you can access in Power BI.",
  },
  dsSearch: { NL: "Zoek werkruimte…", EN: "Search workspace…" },
  dsSuggested: { NL: "Voorstel uit deze werkmap", EN: "Suggested by this workbook" },
  dsLoading: { NL: "Modellen laden…", EN: "Loading models…" },
  dsEmpty: { NL: "Geen modellen gevonden.", EN: "No models found." },
  dsTruncated: {
    NL: "Er zijn meer werkruimten dan getoond: verfijn de zoekopdracht.",
    EN: "There are more workspaces than shown: refine the search.",
  },
  dsOtherModel: { NL: "Geen Auditmart-model", EN: "Not an Auditmart model" },
  dsClear: { NL: "Ontkoppelen", EN: "Disconnect" },
  dsClearTitle: {
    NL: "De gekozen gegevensbron uit deze werkmap halen",
    EN: "Remove the chosen data source from this workbook",
  },
  dsRefreshed: { NL: "vernieuwd {d}", EN: "refreshed {d}" },
  dsKeys: {
    NL: "↑↓ kiezen · Enter bevestigen · Esc sluiten",
    EN: "↑↓ move · Enter confirm · Esc close",
  },
  dsErrSignIn: {
    NL: "Meld je aan in Office om Power BI te gebruiken.",
    EN: "Sign in to Office to use Power BI.",
  },
  dsErrConsent: {
    NL: "De Power BI-machtigingen van SmartSuite zijn nog niet goedgekeurd in Entra. Vraag de beheerder.",
    EN: "The Power BI permissions of SmartSuite are not yet approved in Entra. Ask the administrator.",
  },
  dsErrGuests: {
    NL: "De Power BI-bron is nog niet beschikbaar voor je organisatie.",
    EN: "The Power BI source is not yet available for your organisation.",
  },
  dsErrNotInTenant: {
    NL: "Je account is (nog) geen gast in de AGTT-omgeving van Power BI.",
    EN: "Your account is not (yet) a guest in AGTT's Power BI environment.",
  },
  dsErrNoAccess: {
    NL: "Geen toegang tot dit model in Power BI.",
    EN: "No access to this model in Power BI.",
  },
  dsErrFeature: {
    NL: "Power BI staat query's via de API niet toe (tenant-instelling). Vraag de beheerder.",
    EN: "Power BI does not allow queries through the API (tenant setting). Ask the administrator.",
  },
  dsErrThrottled: {
    NL: "Power BI is even druk; probeer het over {s} s opnieuw.",
    EN: "Power BI is busy; try again in {s} s.",
  },
  dsErrNotConfigured: {
    NL: "De Power BI-bron is op deze server niet ingericht.",
    EN: "The Power BI source is not set up on this server.",
  },
  dsErrGeneric: { NL: "Power BI gaf een fout: {m}", EN: "Power BI returned an error: {m}" },
  dsRetry: { NL: "Opnieuw", EN: "Retry" },
  dsChecking: { NL: "Model controleren…", EN: "Checking the model…" },
  dsReading: { NL: "Werkmap lezen…", EN: "Reading the workbook…" },
  dsCamt: { NL: "CAMT: {n} regels, {from} t/m {to}", EN: "CAMT: {n} rows, {from} to {to}" },
  dsNoCamt: { NL: "Geen CAMT-gegevens in dit model.", EN: "No CAMT data in this model." },
  dsChange: { NL: "Wijzigen", EN: "Change" },
  dsChosenBy: { NL: "Gekozen door {who} op {d}", EN: "Chosen by {who} on {d}" },
  dsTransit: { NL: "Betalingen onderweg", EN: "Payments in transit" },
  dsTransitTitle: {
    NL: "Grootboekrekeningen waarop de klant betalingen boekt voordat de bank ze afschrijft. Zonder bankregel zoekt de betalingstoets de afboeking van de factuur via het bankboek of via deze rekeningen.",
    EN: "Ledger accounts on which the client books payments before the bank debits them. Without a bank line the payment test looks for the invoice settlement via the bank journal or these accounts.",
  },
  dsTransitNone: { NL: "geen rekening gevonden", EN: "no account found" },
  dsTransitProposed: { NL: "voorgesteld", EN: "suggested" },
  dsTransitConfirmed: { NL: "bevestigd", EN: "confirmed" },
  dsTransitConfirm: { NL: "Bevestigen", EN: "Confirm" },
  dsTransitLoading: { NL: "Rekeningen zoeken…", EN: "Finding accounts…" },
} as const;

export type ShellKey = keyof typeof TEXT;

/** Tekst van de schil in de gevraagde taal (Nederlands als er geen taal is). */
export function shellText(
  key: ShellKey,
  lang: Lang = "NL",
  vars?: Record<string, string | number>
): string {
  const s: string = TEXT[key][lang === "EN" ? "EN" : "NL"];
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}
